/**
 * Request sequencing for every async list the UI shows (ux/01 §3.4 « late
 * responses », cross-module 02_DESIGN §3.1 « one AbortController, sequence id —
 * stale responses dropped »).
 *
 * Each `begin()` aborts the previous request and returns a ticket carrying a
 * sequence number and an AbortSignal. A response is applied only while its
 * ticket is still current: a newer query, a blur or an unmount (`cancel()`)
 * makes it stale, so a slow answer can never overwrite a newer one.
 *
 *   const seq = createRequestSequence();
 *   const t = seq.begin();
 *   const data = await api.list(params, { signal: t.signal });
 *   if (!seq.isCurrent(t)) return;   // dropped
 */
export function createRequestSequence() {
  let current = 0;
  let controller = null;
  const makeController = () =>
    typeof AbortController === "function" ? new AbortController() : null;
  return {
    /** Start a request: aborts the one in flight, returns its ticket. */
    begin() {
      controller?.abort();
      controller = makeController();
      current += 1;
      return { id: current, signal: controller?.signal };
    },
    /** True while `ticket` is the latest request and was not cancelled. */
    isCurrent(ticket) {
      return !!ticket && ticket.id === current && !ticket.signal?.aborted;
    },
    /** Invalidate whatever is in flight (blur, unmount, query cleared). */
    cancel() {
      controller?.abort();
      controller = null;
      current += 1;
    },
  };
}

/**
 * True when an error is the abort of a superseded request (never shown to the
 * user). Covers fetch (`AbortError`) and axios (`CanceledError`,
 * `ERR_CANCELED`).
 * @param {unknown} err
 */
export function isAbortError(err) {
  if (!err || typeof err !== "object") return false;
  const e = /** @type {{name?: string, code?: string}} */ (err);
  return (
    e.name === "AbortError" || e.name === "CanceledError" || e.code === "ERR_CANCELED"
  );
}
